import React, { useEffect, useRef } from 'react';

const PIN_LENGTH = 4;

export const PinPad = ({ value = '', onChange, onSubmit, error }) => {
  const safeValue = String(value || '').replace(/[^0-9]/g, '').slice(0, PIN_LENGTH);
  const pinInputRef = useRef(null);

  const appendDigit = (digit) => {
    if (safeValue.length >= PIN_LENGTH) return;
    const nextPin = `${safeValue}${digit}`.slice(0, PIN_LENGTH);
    onChange(nextPin);

    if (nextPin.length === PIN_LENGTH) {
      onSubmit?.(nextPin);
    }
  };

  const removeDigit = () => {
    onChange(safeValue.slice(0, -1));
  };

  const submitNow = () => {
    if (safeValue.length === PIN_LENGTH) {
      onSubmit?.(safeValue);
    }
  };

  const handleInputChange = (event) => {
    onChange(event.target.value.replace(/[^0-9]/g, '').slice(0, PIN_LENGTH));
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitNow();
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      pinInputRef.current?.focus();
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!error) return;

    const timer = setTimeout(() => {
      onChange('');
    }, 350);

    return () => clearTimeout(timer);
  }, [error, onChange]);

  return (
    <div className="space-y-3">
      <input
        ref={pinInputRef}
        type="password"
        inputMode="numeric"
        pattern="[0-9]*"
        value={safeValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        maxLength={PIN_LENGTH}
        autoFocus
        className="w-full rounded-lg border border-gray-300 px-4 py-3 text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />

      <div className={`flex justify-center gap-2 ${error ? 'animate-pulse' : ''}`}>
        {Array.from({ length: PIN_LENGTH }).map((_, index) => (
          <span
            key={index}
            className={`h-3 w-3 rounded-full ${index < safeValue.length ? 'bg-gray-900' : 'bg-gray-300'}`}
          />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
          <button
            key={digit}
            type="button"
            onClick={() => appendDigit(String(digit))}
            className="h-14 min-h-[56px] rounded-lg border border-gray-300 bg-white text-lg font-semibold hover:bg-gray-50"
          >
            {digit}
          </button>
        ))}

        <button
          type="button"
          onClick={removeDigit}
          className="h-14 min-h-[56px] rounded-lg border border-gray-300 bg-white text-sm font-semibold hover:bg-gray-50"
        >
          ⌫
        </button>
        <button
          type="button"
          onClick={() => appendDigit('0')}
          className="h-14 min-h-[56px] rounded-lg border border-gray-300 bg-white text-lg font-semibold hover:bg-gray-50"
        >
          0
        </button>
        <button
          type="button"
          onClick={submitNow}
          disabled={safeValue.length !== PIN_LENGTH}
          className="h-14 min-h-[56px] rounded-lg border border-green-300 bg-green-50 text-lg font-semibold text-green-700 hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          ✓
        </button>
      </div>

      {error && <p className="text-sm text-red-600 text-center">{error}</p>}
    </div>
  );
};

export default PinPad;
